import ExpandIcon from '@mui/icons-material/ExpandMore';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { runMultiplexHistogram } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { HistogramJob, JobType } from 'interfaces/job';
import { MultiplexHistogramConfig } from 'interfaces/jobs/multiplex/histogramParams';
import { Permission } from 'interfaces/permissionOption';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import * as yup from 'yup';
import { ActionStepper } from '../ActionStepper';
import { JobWithRebuild } from '../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import { JobNameAndDescriptionStep } from '../utils';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import HistogramConfig, { defaultHistogramConfig, validationHistogramConfig } from './RunHistogram/HistogramParams';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

const SNACK_BAR_KEY_RUN_MULTIPLEX_HISTOGRAM = 'RUN_MULTIPLEX_HiSTOGRAM';

export interface HistogramFormValues {
  jobName: string;
  jobDescription: string;
  configParams: MultiplexHistogramConfig;
}

const validationSchema = [
  yup.object({}),
  yup.object({}),
  yup.object({
    configParams: validationHistogramConfig,
  }),
  yup.object({}),
];

export interface RunMultiplexHistogramProps {
  onClose: (event?: any, force?: boolean) => void;
  jobId?: string;
  casesParams: CasesParams;
}

export const RunMultiplexHistogram = (props: RunMultiplexHistogramProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState<string>(jobId ?? undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);

  const currentValidationSchema = validationSchema[activeStep];

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const {
    reset,
    getValues,
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<HistogramFormValues>({
    mode: 'onChange',
    defaultValues: {
      jobName: null,
      jobDescription: null,
      configParams: defaultHistogramConfig,
    },
    resolver: yupResolver(currentValidationSchema),
  });
  useTrackActionFormDirty(isDirty);

  const checkValidationAndSetIsStepFailed = (stepIndex: number, objectToValidate: Record<string, any>) => {
    validationSchema[stepIndex]
      .validate(objectToValidate)
      .then(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: false,
        }));
      })
      .catch(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: true,
        }));
      });
  };

  const runMultiplexHistogramMutation = useMutationWithErrorSnackbar({
    mutationFn: runMultiplexHistogram,
    mutationDescription: 'run multiplex histogram',
    onSuccess: () => {
      enqueueSnackbar('Multiplex Histogram Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_HISTOGRAM);
    },
  });

  const onSubmit: SubmitHandler<HistogramFormValues> = async (data) => {
    runMultiplexHistogramMutation.mutate({
      ...multiplexCasesParams,
      jobName: data.jobName,
      jobDescription: data.jobDescription,
      configParams: data.configParams,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Multiplex Histogram to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_HISTOGRAM,
      autoHideDuration: null,
    });

    onClose(undefined, true);
  };

  const validateConfigurationStep = async () => {
    checkValidationAndSetIsStepFailed(2, {
      configParams: watch('configParams'),
    });
  };

  const onSelectedJobParamChange = (newValue: HistogramJob) => {
    if (isEmpty(newValue)) {
      setSelectedJobId(undefined);
      reset({
        jobName: null,
        jobDescription: null,
        configParams: defaultHistogramConfig,
      });
    } else {
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        configParams: newValue?.params,
      });

      validateConfigurationStep();

      setSelectedJobId(newValue.id);
      if (!('params' in newValue)) {
        console.warn('histogram params not found in job params');
      }
      if (!('name' in newValue)) {
        console.warn('name not found in job params');
      }
      if (!('description' in newValue)) {
        console.warn('description not found in job params');
      }
    }
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.MultiplexHistogram);

  const steps = [
    {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.MultiplexHistogram}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    {
      label: 'Histogram Configuration',
      optional: true,
      content: (
        <HistogramConfig
          histogramConfig={watch('configParams') ?? ({} as MultiplexHistogramConfig)}
          setHistogramConfig={(histogramConfig) => {
            reset({
              ...getValues(),
              configParams: histogramConfig,
            });

            validateConfigurationStep();
          }}
          casesParams={multiplexCasesParams}
        />
      ),
      onNextOrBackClick: () => validateConfigurationStep(),
    },
    {
      label: 'Advanced',
      optional: true,
      content: (
        <Grid container>
          <Grid item xs={2}>
            <TextField
              label="Max Workers"
              type="number"
              InputProps={{ inputProps: { min: 0, max: 16 } }}
              value={watch('configParams.maxWorkers') ?? 16}
              onChange={(event) =>
                reset({
                  ...getValues(),
                  configParams: { ...getValues().configParams, maxWorkers: Number(event.target.value) },
                })
              }
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <Grid container direction="column" spacing={2}>
        {multiplexSelectionInfoMessage ? (
          <Grid item>
            <MultiplexSelectionAlert
              severity="info"
              title="This action runs on slides"
              message={multiplexSelectionInfoMessage}
            />
          </Grid>
        ) : null}
        <Grid item>
          <ActionStepper
            handleSubmit={handleSubmit(onSubmit)}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
          />
        </Grid>
        {canViewJsonJobParams && (
          <Grid item>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandIcon />}>Histogram Params Summary (JSON)</AccordionSummary>
              <AccordionDetails>
                <Typography component="pre">
                  {JSON.stringify(
                    {
                      jobName: watch('jobName'),
                      jobDescription: watch('jobDescription'),
                      configParams: watch('configParams'),
                    },
                    null,
                    2
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </JobWithRebuild>
  );
};
